import { useCallback, useMemo } from 'react';
import { PhoneNumbersV1 } from '@frontend/api-phone-numbers';
import { LocationPhone, LocationPhonesData } from '../types';
import { formatProtobufPhone } from '../utils';
import { useInboxLocationIds } from './use-inbox-location-ids';

type UseLocationPhonesArgs = {
  locationIds?: string[];
  filterLocationPhones?: string[];
};

type UseLocationPhonesReturn = {
  data: LocationPhonesData;
  enabledData: LocationPhonesData;
  isLoading: boolean;
  outboundSmsIdEnabledPhones: string[];
  /**
   * Helper function to check if a specific location phone number has AI enabled.
   * Checks against all location phones managed by this hook.
   * @param locationPhone - The phone number to check
   * @returns true if the phone has AI enabled, false otherwise
   */
  checkIsLocationPhoneAiEnabled: (locationPhone: string | undefined) => boolean;
};

/**
 * Hook for fetching and managing outbound phone numbers available in the inbox.
 *
 * This hook provides a unified interface for retrieving either all sms-enabled phone numbers, or only
 * department-related phone numbers SMS numbers or general location phone numbers, depending on feature flag
 * configuration. It handles ACL filtering to ensure users only see phone numbers for the locations that they have
 * inbox permissions for.
 *
 * @param locationIds - Optional array of location IDs to filter phone numbers.
 *   If not provided, uses currently selected location IDs.
 *
 * @returns Object containing:
 *   - `data`: The location phones data
 *
 * @remarks
 * - Phone numbers are formatted to E164 standard for consistency
 * - Queries are conditionally enabled based on feature flag state and location availability
 */
export const useLocationPhones = ({
  locationIds: providedLocationIds,
  filterLocationPhones = [],
}: UseLocationPhonesArgs = {}): UseLocationPhonesReturn => {
  const locationIds = useInboxLocationIds({ locationIds: providedLocationIds });

  // For private comms
  const locationPhonesQuery = PhoneNumbersV1.Queries.useListUserAccessiblePhoneNumbersQuery({
    request: {
      locationIds,
    },
    options: {
      enabled: locationIds.length > 0,
    },
  });

  const outboundSmsNumbersQuery = PhoneNumbersV1.Queries.useListOutboundSmsNumbers(
    {
      locationIds,
    },
    {
      enabled: locationIds.length > 0,
    }
  );

  // For private comms, filter outbound SMS numbers to only include those present in user accessible phone numbers
  // If locationPhonesQuery fails or is loading, return empty array to prevent showing unverified numbers
  const outboundSmsNumbers = useMemo(() => {
    // If location phones query hasn't succeeded, don't show any outbound numbers
    if (!locationPhonesQuery.isSuccess || !locationPhonesQuery.data?.phoneNumbers) {
      return [];
    }

    // If outbound SMS query hasn't loaded yet or failed, return empty array
    if (!outboundSmsNumbersQuery.data) {
      return [];
    }

    // Create a Set of accessible phone numbers for efficient lookup
    const accessiblePhoneNumbers = new Set(
      locationPhonesQuery.data.phoneNumbers.map((phone) => formatProtobufPhone(phone.phoneNumber)).filter(Boolean)
    );

    // Filter outbound SMS numbers to only include those in the accessible set
    return outboundSmsNumbersQuery.data
      .map(({ locationId, outboundSmsNumbers: currentOutboundSmsNumbers }) => ({
        locationId,
        outboundSmsNumbers: (currentOutboundSmsNumbers || []).filter(({ number }) => {
          // Only include if the number exists in user's accessible phones
          return number && accessiblePhoneNumbers.has(number);
        }),
      }))
      .filter((entry) => entry.outboundSmsNumbers.length > 0); // Remove locations with no matching numbers
  }, [locationPhonesQuery.isSuccess, locationPhonesQuery.data?.phoneNumbers, outboundSmsNumbersQuery.data]);

  const filterLocationPhonesStr = filterLocationPhones.join(',');
  const allLocationPhonesData = useMemo<LocationPhonesData>(() => {
    // v4 - outbound sms numbers
    return (
      locationPhonesQuery.data?.phoneNumbers?.reduce<LocationPhonesData>(
        (acc, curr) => {
          if (!locationIds.includes(curr.locationId)) {
            return acc;
          }
          const currentEntries = acc.locationPhonesByLocation[curr.locationId] ?? [];
          const locationPhone = formatProtobufPhone(curr.phoneNumber);

          if (!locationPhone || filterLocationPhonesStr.includes(locationPhone)) {
            return acc;
          }

          const newEntry: LocationPhone = {
            locationPhone,
            locationId: curr.locationId,
            displayName: curr.displayName,
            defaultSms: curr.defaultSms,
            defaultAi: curr.defaultAi,
          };

          acc.locationPhonesByLocation[curr.locationId] = [...currentEntries, newEntry];
          acc.allLocationPhones.push(newEntry);
          return acc;
        },
        { locationPhonesByLocation: {}, allLocationPhones: [] }
      ) ?? { locationPhonesByLocation: {}, allLocationPhones: [] }
    );
  }, [filterLocationPhonesStr, locationIds, locationPhonesQuery.data?.phoneNumbers]);

  const enabledOutboundPhoneNumbers = useMemo<LocationPhonesData>(() => {
    // v4 - outbound sms numbers (filtered by user accessible phones)
    return outboundSmsNumbers
      .map(({ locationId, outboundSmsNumbers: currentOutboundSmsNumbers }) =>
        (currentOutboundSmsNumbers || []).map((number) => ({ ...number, locationId }))
      )
      .flat()
      .reduce<LocationPhonesData>(
        (acc, curr) => {
          if (!locationIds.includes(curr.locationId)) {
            return acc;
          }

          const currentEntries = acc.locationPhonesByLocation[curr.locationId] ?? [];

          if (!curr.number || filterLocationPhonesStr.includes(curr.number)) {
            return acc;
          }

          const newEntry: LocationPhone = {
            locationPhone: curr.number,
            locationId: curr.locationId,
            displayName: curr.displayName,
            defaultSms: curr.default,
          };

          acc.locationPhonesByLocation[curr.locationId] = [...currentEntries, newEntry];
          acc.allLocationPhones.push(newEntry);
          return acc;
        },
        { locationPhonesByLocation: {}, allLocationPhones: [] }
      );
  }, [filterLocationPhonesStr, locationIds, outboundSmsNumbers]);

  const outboundSmsIdEnabledPhones = useMemo(
    () =>
      locationPhonesQuery.data?.phoneNumbers?.reduce<string[]>((acc, curr) => {
        if (curr.capabilities?.smsCapabilities?.outboundSmsIdEnabled) {
          const formatted = formatProtobufPhone(curr.phoneNumber);
          if (formatted) acc.push(formatted);
        }
        return acc;
      }, []) ?? [],
    [locationPhonesQuery.data?.phoneNumbers]
  );

  // Helper function that checks against this hook's internal data
  const checkIsLocationPhoneAiEnabled = useCallback(
    (locationPhone: string | undefined) => {
      if (!locationPhone || !allLocationPhonesData.allLocationPhones) {
        return false;
      }
      return allLocationPhonesData.allLocationPhones.some(
        (locationPhoneObj) => locationPhoneObj.locationPhone === locationPhone && locationPhoneObj.defaultAi === true
      );
    },
    [allLocationPhonesData.allLocationPhones]
  );

  return {
    data: allLocationPhonesData,
    enabledData: enabledOutboundPhoneNumbers,
    isLoading: locationPhonesQuery.isLoading,
    outboundSmsIdEnabledPhones,
    checkIsLocationPhoneAiEnabled,
  };
};
